import { createContext, useContext, useEffect, useState } from 'react';
import './cognitoConfig'; // This configures Amplify
import { 
  signUp, 
  signIn, 
  signOut as amplifySignOut, 
  getCurrentUser, 
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  confirmSignUp,
  resendSignUpCode
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const AuthContext = createContext(undefined);

export const AuthContextProvider = ({ children }) => {
  const [session, setSession] = useState(undefined);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);

  const resetPassword = async (email) => {
    try {
      await amplifyResetPassword({ username: email.toLowerCase() });
      localStorage.setItem('reset_email', email.toLowerCase());
      return { success: true };
    } catch (err) {
      setIsEmailConfirmed(false);
      return { error: err };
    }
  };

  const updatePassword = async (newPassword, code) => {
    try {
      const email = localStorage.getItem('reset_email') || '';
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      return { success: true };
    } catch (err) {
      return { error: err };
    }
  };

  const resendConfirmationEmail = async (email) => {
    try {
      await resendSignUpCode({ username: email.toLowerCase() });
      return { success: true };
    } catch (err) {
      console.error('Error resending confirmation code:', err);
      return { success: false, error: err.message || 'Failed to resend confirmation email' };
    }
  };

  const signUpNewUser = async (email, password, name) => {
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email.toLowerCase(),
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase(),
            name: name
          }
        }
      });
      localStorage.setItem('signup_email', email.toLowerCase());
      return { success: true, data: { isSignUpComplete, userId, nextStep } };
    } catch (error) {
      console.error('Error signing up: ', error);
      return { success: false, error };
    }
  };

  const verifySignUpCode = async (email, code) => {
    try {
      await confirmSignUp({
        username: email.toLowerCase(),
        confirmationCode: code
      });
      return { success: true };
    } catch (err) {
      console.error('Verification error:', err);
      return { success: false, error: err.message || err };
    }
  };

  const signInUser = async (email, password) => {
    try {
      // Ensure any existing or stale session is cleared first
      try {
        await amplifySignOut();
      } catch (_) {
        // Ignore sign-out errors if no user was signed in
      }

      const signInOutput = await signIn({
        username: email.toLowerCase(),
        password
      });

      if (signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        localStorage.setItem('signup_email', email.toLowerCase());
        return { success: false, error: 'Please confirm your sign up first.', needsConfirmation: true };
      }

      const currentUser = await getCurrentUser();
      const authSession = await fetchAuthSession();
      const attributes = await fetchUserAttributes();
      const token = authSession.tokens?.idToken?.toString() || '';

      const data = {
        session: {
          access_token: token,
          user: {
            id: currentUser.userId,
            email: attributes.email,
            user_metadata: {
              display_name: attributes.name || attributes.email,
              onboarded: true
            }
          }
        }
      };

      try {
        const payload = {
          userName: data.session.user.user_metadata.display_name,
          userEmail: data.session.user.email,
          userId: data.session.user.id,
          subscription_type: 'free', 
        };
      
        await fetch(process.env.REACT_APP_API_URL + "/send_notification_mail", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Userid": data.session.user.id,
            "Useremail": data.session.user.email
          },
          body: JSON.stringify(payload)
        });
      } catch (notificationError) {
        console.error('Notification API error:', notificationError);
      }

      return { success: true, data };
    } catch (err) {
      console.error('Unexpected error during sign-in:', err.message || err);
      // If still encountering user already signed in, attempt sign out and retry once
      if (err.message?.includes('already a signed in user') || err.name === 'UserAlreadyAuthenticatedException') {
        try {
          await amplifySignOut();
          const retrySignIn = await signIn({
            username: email.toLowerCase(),
            password
          });
          const currentUser = await getCurrentUser();
          const authSession = await fetchAuthSession();
          const attributes = await fetchUserAttributes();
          const token = authSession.tokens?.idToken?.toString() || '';
          return {
            success: true,
            data: {
              session: {
                access_token: token,
                user: {
                  id: currentUser.userId,
                  email: attributes.email,
                  user_metadata: {
                    display_name: attributes.name || attributes.email,
                    onboarded: true
                  }
                }
              }
            }
          };
        } catch (retryErr) {
          return {
            success: false,
            error: retryErr.message || 'An unexpected error occurred. Please try again.'
          };
        }
      }

      return {
        success: false,
        error: err.message || 'An unexpected error occurred. Please try again.'
      };
    }
  };

  // DISABLED: OAuth temporarily removed during Cognito migration.
  // Email/password authentication only.
  const signInWithGoogle = async () => {
    return { error: new Error('Google OAuth disabled during Cognito migration.') };
  };

  // DISABLED: OAuth temporarily removed during Cognito migration.
  // Email/password authentication only.
  const signInWithGithub = async () => {
    return { error: new Error('GitHub OAuth disabled during Cognito migration.') };
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user_mailId");
    localStorage.removeItem("user_name");
    localStorage.removeItem("session_id");
    localStorage.removeItem("signup_email");
    localStorage.removeItem("reset_email");
    setSession(null);
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const currentUser = await getCurrentUser();
        const authSession = await fetchAuthSession();
        const attributes = await fetchUserAttributes();
        const token = authSession.tokens?.idToken?.toString();
        if (token) {
          setSession({
            access_token: token,
            user: {
              id: currentUser.userId,
              email: attributes.email,
              user_metadata: {
                display_name: attributes.name || attributes.email,
                onboarded: true
              }
            }
          });
        } else {
          setSession(null);
        }
      } catch (err) {
        setSession(null);
      }
    };

    checkSession();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        checkSession();
      } else if (payload.event === 'signedOut') {
        setSession(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const checkEmailConfirmation = async () => {
    try {
      const attributes = await fetchUserAttributes();
      return attributes.email_verified === 'true';
    } catch (err) {
      console.error('Error checking email confirmation:', err);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signUpNewUser,
        verifySignUpCode,
        signInUser,
        session,
        signOut,
        isEmailConfirmed,
        checkEmailConfirmation,
        resendConfirmationEmail,
        resetPassword,
        updatePassword,
        signInWithGoogle,
        signInWithGithub
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('UserAuth must be used within an AuthContextProvider');
  return context;
};